import backendUrl from "../../BackEndURL";

const CACHE_VERSION = "v4";
const DB_NAME = "TamilHolyBibleDownloadCache";
const DB_VERSION = 1;
const BOOK_STORE_NAME = "books";
const META_STORE_NAME = "meta";

const VERSE_CACHE_PREFIX = "tamilHolyBible:testament:verses";
const STATUS_STORAGE_KEY = `tamilHolyBible:downloadCache:status:${CACHE_VERSION}`;
const COMPLETE_STORAGE_KEY = `tamilHolyBible:downloadCache:complete:${CACHE_VERSION}`;

const BOOK_FALLBACK_BATCH_SIZE = 5;
const BOOK_FALLBACK_BATCH_GAP = 45;
const CACHE_DOWNLOAD_CONCURRENCY = 2;

const DEFAULT_STATUS = {
  status: "idle",
  percent: 0,
  completed: 0,
  total: 0,
  errors: 0,
  message: "",
};

const TESTAMENT_CONFIG = {
  old: {
    sectionId: 1,
    testament: "Old Testament",
  },
  new: {
    sectionId: 2,
    testament: "New Testament",
  },
};

const STATIC_OLD_TESTAMENT_BOOKS = [
  "Genesis",
  "Exodus",
  "Leviticus",
  "Numbers",
  "Deuteronomy",
  "Joshua",
  "Judges",
  "Ruth",
  "1 Samuel",
  "2 Samuel",
  "1 Kings",
  "2 Kings",
  "1 Chronicles",
  "2 Chronicles",
  "Ezra",
  "Nehemiah",
  "Esther",
  "Job",
  "Psalms",
  "Proverbs",
  "Ecclesiastes",
  "Song of Solomon",
  "Isaiah",
  "Jeremiah",
  "Lamentations",
  "Ezekiel",
  "Daniel",
  "Hosea",
  "Joel",
  "Amos",
  "Obadiah",
  "Jonah",
  "Micah",
  "Nahum",
  "Habakkuk",
  "Zephaniah",
  "Haggai",
  "Zechariah",
  "Malachi",
];

const STATIC_NEW_TESTAMENT_BOOKS = [
  "Matthew",
  "Mark",
  "Luke",
  "John",
  "Acts",
  "Romans",
  "1 Corinthians",
  "2 Corinthians",
  "Galatians",
  "Ephesians",
  "Philippians",
  "Colossians",
  "1 Thessalonians",
  "2 Thessalonians",
  "1 Timothy",
  "2 Timothy",
  "Titus",
  "Philemon",
  "Hebrews",
  "James",
  "1 Peter",
  "2 Peter",
  "1 John",
  "2 John",
  "3 John",
  "Jude",
  "Revelation",
];

const STATIC_CHAPTER_COUNTS = {
  genesis: 50,
  exodus: 40,
  leviticus: 27,
  numbers: 36,
  deuteronomy: 34,
  joshua: 24,
  judges: 21,
  ruth: 4,

  "1samuel": 31,
  isamuel: 31,
  firstsamuel: 31,

  "2samuel": 24,
  iisamuel: 24,
  secondsamuel: 24,

  "1kings": 22,
  ikings: 22,
  firstkings: 22,

  "2kings": 25,
  iikings: 25,
  secondkings: 25,

  "1chronicles": 29,
  ichronicles: 29,
  firstchronicles: 29,

  "2chronicles": 36,
  iichronicles: 36,
  secondchronicles: 36,

  ezra: 10,
  nehemiah: 13,
  esther: 10,
  job: 42,
  psalms: 150,
  psalm: 150,
  proverbs: 31,
  ecclesiastes: 12,
  songofsolomon: 8,
  songofsongs: 8,
  canticles: 8,
  isaiah: 66,
  jeremiah: 52,
  lamentations: 5,
  ezekiel: 48,
  daniel: 12,
  hosea: 14,
  joel: 3,
  amos: 9,
  obadiah: 1,
  jonah: 4,
  micah: 7,
  nahum: 3,
  habakkuk: 3,
  zephaniah: 3,
  haggai: 2,
  zechariah: 14,
  malachi: 4,

  matthew: 28,
  mark: 16,
  luke: 24,
  john: 21,
  acts: 28,
  actsoftheapostles: 28,
  romans: 16,

  "1corinthians": 16,
  icorinthians: 16,
  firstcorinthians: 16,

  "2corinthians": 13,
  iicorinthians: 13,
  secondcorinthians: 13,

  galatians: 6,
  ephesians: 6,
  philippians: 4,
  colossians: 4,

  "1thessalonians": 5,
  ithessalonians: 5,
  firstthessalonians: 5,

  "2thessalonians": 3,
  iithessalonians: 3,
  secondthessalonians: 3,

  "1timothy": 6,
  itimothy: 6,
  firsttimothy: 6,

  "2timothy": 4,
  iitimothy: 4,
  secondtimothy: 4,

  titus: 3,
  philemon: 1,
  hebrews: 13,
  james: 5,

  "1peter": 5,
  ipeter: 5,
  firstpeter: 5,

  "2peter": 3,
  iipeter: 3,
  secondpeter: 3,

  "1john": 5,
  ijohn: 5,
  firstjohn: 5,

  "2john": 1,
  iijohn: 1,
  secondjohn: 1,

  "3john": 1,
  iiijohn: 1,
  thirdjohn: 1,

  jude: 1,
  revelation: 22,
  revelations: 22,
};

const SECTION_TYPE_MAP = {
  1: "old",
  2: "new",
};

const readJsonFromLocalStorage = (key) => {
  try {
    const value = localStorage.getItem(key);

    if (!value) {
      return null;
    }

    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const writeJsonToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore localStorage errors.
  }
};

const getPersistedStatus = () => {
  const savedStatus = readJsonFromLocalStorage(STATUS_STORAGE_KEY);

  if (!savedStatus || savedStatus.cacheVersion !== CACHE_VERSION) {
    return { ...DEFAULT_STATUS };
  }

  return {
    status: savedStatus.status || "idle",
    percent: Number(savedStatus.percent || 0),
    completed: Number(savedStatus.completed || 0),
    total: Number(savedStatus.total || 0),
    errors: Number(savedStatus.errors || 0),
    message: savedStatus.message || "",
  };
};

let localStatus = getPersistedStatus();
let localListeners = new Set();
let downloadPromise = null;
let indexedDbPromise = null;

const normalizeBookKey = (bookName) => {
  return String(bookName || "").trim().toLowerCase();
};

const getStaticChapterCountKey = (bookName) => {
  return String(bookName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
};

const getChapterCount = (bookName) => {
  const key = getStaticChapterCountKey(bookName);

  return STATIC_CHAPTER_COUNTS[key] || 0;
};

const getBookStorageKey = (testamentType, book) => {
  return `${CACHE_VERSION}:${testamentType}:${normalizeBookKey(book)}`;
};

const getVerseCacheKey = (testamentType, book, chapter) => {
  return `${VERSE_CACHE_PREFIX}:${CACHE_VERSION}:${testamentType}:${normalizeBookKey(
    book
  )}:${chapter}`;
};

const wait = (milliseconds) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const getStore = () => {
  if (typeof window === "undefined") {
    return {
      status: localStatus,
      listeners: localListeners,
      promise: downloadPromise,
    };
  }

  if (!window.__TAMIL_HOLY_BIBLE_DOWNLOAD_CACHE_STORE__) {
    window.__TAMIL_HOLY_BIBLE_DOWNLOAD_CACHE_STORE__ = {
      status: getPersistedStatus(),
      listeners: new Set(),
      promise: null,
    };
  }

  return window.__TAMIL_HOLY_BIBLE_DOWNLOAD_CACHE_STORE__;
};

const getDownloadPromise = () => {
  return getStore().promise;
};

const setDownloadPromise = (promise) => {
  const store = getStore();

  store.promise = promise;

  if (typeof window === "undefined") {
    downloadPromise = promise;
  }
};

const emitStatus = () => {
  const store = getStore();
  const snapshot = { ...store.status };

  store.listeners.forEach((listener) => {
    listener(snapshot);
  });
};

const setStatus = (nextStatus) => {
  const store = getStore();

  store.status = {
    ...store.status,
    ...nextStatus,
  };

  if (typeof window === "undefined") {
    localStatus = store.status;
  }

  writeJsonToLocalStorage(STATUS_STORAGE_KEY, {
    ...store.status,
    cacheVersion: CACHE_VERSION,
    savedAt: Date.now(),
  });

  emitStatus();
};

const getGlobalVerseMemoryCache = () => {
  if (typeof window === "undefined") {
    return new Map();
  }

  if (!window.__TAMIL_HOLY_BIBLE_VERSE_MEMORY_CACHE__) {
    window.__TAMIL_HOLY_BIBLE_VERSE_MEMORY_CACHE__ = new Map();
  }

  return window.__TAMIL_HOLY_BIBLE_VERSE_MEMORY_CACHE__;
};

const getGlobalWholeBookMemoryCache = () => {
  if (typeof window === "undefined") {
    return new Map();
  }

  if (!window.__TAMIL_HOLY_BIBLE_WHOLE_BOOK_MEMORY_CACHE__) {
    window.__TAMIL_HOLY_BIBLE_WHOLE_BOOK_MEMORY_CACHE__ = new Map();
  }

  return window.__TAMIL_HOLY_BIBLE_WHOLE_BOOK_MEMORY_CACHE__;
};

const getGlobalPendingVerseRequests = () => {
  if (typeof window === "undefined") {
    return new Map();
  }

  if (!window.__TAMIL_HOLY_BIBLE_PENDING_VERSE_REQUESTS__) {
    window.__TAMIL_HOLY_BIBLE_PENDING_VERSE_REQUESTS__ = new Map();
  }

  return window.__TAMIL_HOLY_BIBLE_PENDING_VERSE_REQUESTS__;
};

const getGlobalPendingWholeBookRequests = () => {
  if (typeof window === "undefined") {
    return new Map();
  }

  if (!window.__TAMIL_HOLY_BIBLE_PENDING_WHOLE_BOOK_REQUESTS__) {
    window.__TAMIL_HOLY_BIBLE_PENDING_WHOLE_BOOK_REQUESTS__ = new Map();
  }

  return window.__TAMIL_HOLY_BIBLE_PENDING_WHOLE_BOOK_REQUESTS__;
};

const getGlobalBookPrefetchRequests = () => {
  if (typeof window === "undefined") {
    return new Map();
  }

  if (!window.__TAMIL_HOLY_BIBLE_BOOK_PREFETCH_REQUESTS__) {
    window.__TAMIL_HOLY_BIBLE_BOOK_PREFETCH_REQUESTS__ = new Map();
  }

  return window.__TAMIL_HOLY_BIBLE_BOOK_PREFETCH_REQUESTS__;
};

const openBibleIndexedDb = () => {
  if (typeof window === "undefined" || !window.indexedDB) {
    return Promise.resolve(null);
  }

  if (indexedDbPromise) {
    return indexedDbPromise;
  }

  indexedDbPromise = new Promise((resolve) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(BOOK_STORE_NAME)) {
        db.createObjectStore(BOOK_STORE_NAME, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(META_STORE_NAME)) {
        db.createObjectStore(META_STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      resolve(null);
    };

    request.onblocked = () => {
      resolve(null);
    };
  });

  return indexedDbPromise;
};

const getFromIndexedDb = async (storeName, key) => {
  const db = await openBibleIndexedDb();

  if (!db) {
    return null;
  }

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(storeName, "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        resolve(null);
      };
    } catch (error) {
      resolve(null);
    }
  });
};

const setToIndexedDb = async (storeName, value) => {
  const db = await openBibleIndexedDb();

  if (!db) {
    return false;
  }

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(storeName, "readwrite");
      const store = transaction.objectStore(storeName);
      const request = store.put(value);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        resolve(false);
      };
    } catch (error) {
      resolve(false);
    }
  });
};

const countIndexedDbStore = async (storeName) => {
  const db = await openBibleIndexedDb();

  if (!db) {
    return 0;
  }

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(storeName, "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => {
        resolve(Number(request.result || 0));
      };

      request.onerror = () => {
        resolve(0);
      };
    } catch (error) {
      resolve(0);
    }
  });
};

const resolveTestamentType = (testamentOrSection) => {
  if (testamentOrSection === "old" || testamentOrSection === "new") {
    return testamentOrSection;
  }

  return SECTION_TYPE_MAP[String(testamentOrSection)] || null;
};

const normalizeVerseWithChapter = (verse, chapterNumber) => {
  return {
    ...verse,
    chapter_number:
      verse?.chapter_number ||
      verse?.chapter ||
      verse?.chapterNumber ||
      chapterNumber,
  };
};

const readCachedVerses = (testamentType, book, chapter) => {
  const cacheKey = getVerseCacheKey(testamentType, book, chapter);
  const memoryCache = getGlobalVerseMemoryCache();

  if (memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey);
  }

  return null;
};

const saveChapterVersesToMemoryOnly = (
  testamentType,
  book,
  chapter,
  verses
) => {
  if (!Array.isArray(verses) || verses.length === 0) {
    return;
  }

  const cacheKey = getVerseCacheKey(testamentType, book, chapter);
  const memoryCache = getGlobalVerseMemoryCache();

  memoryCache.set(cacheKey, verses);
};

const readWholeBookMemoryCache = (testamentType, book) => {
  const bookKey = normalizeBookKey(book);
  const storageKey = getBookStorageKey(testamentType, bookKey);
  const wholeBookCache = getGlobalWholeBookMemoryCache();

  if (wholeBookCache.has(storageKey)) {
    return wholeBookCache.get(storageKey);
  }

  return null;
};

const saveWholeBookMemoryCache = (testamentType, book, verses) => {
  if (!Array.isArray(verses) || verses.length === 0) {
    return;
  }

  const bookKey = normalizeBookKey(book);
  const storageKey = getBookStorageKey(testamentType, bookKey);
  const wholeBookCache = getGlobalWholeBookMemoryCache();

  wholeBookCache.set(storageKey, verses);

  const groupedByChapter = verses.reduce((grouped, verse) => {
    const chapterNumber = Number(verse?.chapter_number || 0);

    if (!chapterNumber) {
      return grouped;
    }

    if (!grouped[chapterNumber]) {
      grouped[chapterNumber] = [];
    }

    grouped[chapterNumber].push(verse);

    return grouped;
  }, {});

  Object.entries(groupedByChapter).forEach(([chapterNumber, chapterVerses]) => {
    saveChapterVersesToMemoryOnly(
      testamentType,
      bookKey,
      chapterNumber,
      chapterVerses
    );
  });
};

const readWholeBookIndexedDbCache = async (testamentType, book) => {
  const bookKey = normalizeBookKey(book);
  const storageKey = getBookStorageKey(testamentType, bookKey);

  const cachedBook = await getFromIndexedDb(BOOK_STORE_NAME, storageKey);

  if (!cachedBook || !Array.isArray(cachedBook.verses)) {
    return null;
  }

  saveWholeBookMemoryCache(testamentType, bookKey, cachedBook.verses);

  return cachedBook.verses;
};

const saveWholeBookIndexedDbCache = async (testamentType, book, verses) => {
  if (!Array.isArray(verses) || verses.length === 0) {
    return false;
  }

  const bookKey = normalizeBookKey(book);
  const storageKey = getBookStorageKey(testamentType, bookKey);

  saveWholeBookMemoryCache(testamentType, bookKey, verses);

  return setToIndexedDb(BOOK_STORE_NAME, {
    id: storageKey,
    cacheVersion: CACHE_VERSION,
    testamentType,
    bookName: bookKey,
    savedAt: Date.now(),
    verses,
  });
};

const fetchJsonSafely = async (url) => {
  const response = await fetch(url);

  if (!response.ok) {
    return null;
  }

  return response.json();
};

const fetchAndCacheChapterVerses = (
  testamentType,
  bookName,
  chapterNumber
) => {
  const config = TESTAMENT_CONFIG[testamentType];

  if (!config) {
    return Promise.resolve([]);
  }

  const bookKey = normalizeBookKey(bookName);
  const cachedVerses = readCachedVerses(testamentType, bookKey, chapterNumber);

  if (cachedVerses && cachedVerses.length > 0) {
    return Promise.resolve(cachedVerses);
  }

  const cacheKey = getVerseCacheKey(testamentType, bookKey, chapterNumber);
  const pendingRequests = getGlobalPendingVerseRequests();

  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const requestPromise = fetchJsonSafely(
    `${backendUrl}/api/sections/${config.sectionId}/${bookKey}/${chapterNumber}`
  )
    .then((data) => {
      const apiVerses = Array.isArray(data?.verses) ? data.verses : [];

      const normalizedVerses = apiVerses.map((verse) =>
        normalizeVerseWithChapter(verse, chapterNumber)
      );

      if (normalizedVerses.length > 0) {
        saveChapterVersesToMemoryOnly(
          testamentType,
          bookKey,
          chapterNumber,
          normalizedVerses
        );
      }

      return normalizedVerses;
    })
    .catch(() => {
      return [];
    })
    .finally(() => {
      pendingRequests.delete(cacheKey);
    });

  pendingRequests.set(cacheKey, requestPromise);

  return requestPromise;
};

const fetchWholeBookFromSingleEndpoint = async (
  testamentType,
  sectionId,
  bookKey
) => {
  const data = await fetchJsonSafely(
    `${backendUrl}/api/sections/${sectionId}/${bookKey}`
  );

  const chapters = Array.isArray(data?.chapters) ? data.chapters : [];

  if (chapters.length === 0) {
    return [];
  }

  return chapters.reduce((allVerses, chapter, index) => {
    const chapterNumber =
      chapter?.chapter_number ||
      chapter?.chapter ||
      chapter?.chapterNumber ||
      index + 1;

    const chapterVerses = Array.isArray(chapter?.verses)
      ? chapter.verses.map((verse) =>
          normalizeVerseWithChapter(verse, chapterNumber)
        )
      : [];

    if (chapterVerses.length > 0) {
      saveChapterVersesToMemoryOnly(
        testamentType,
        bookKey,
        chapterNumber,
        chapterVerses
      );
    }

    return allVerses.concat(chapterVerses);
  }, []);
};

const fetchWholeBookByChaptersFallback = async (testamentType, bookKey) => {
  const chapterCount = getChapterCount(bookKey);

  if (!chapterCount) {
    return [];
  }

  const allVerses = [];

  for (
    let startIndex = 0;
    startIndex < chapterCount;
    startIndex += BOOK_FALLBACK_BATCH_SIZE
  ) {
    const chapterNumbers = Array.from(
      {
        length: Math.min(BOOK_FALLBACK_BATCH_SIZE, chapterCount - startIndex),
      },
      (_, index) => startIndex + index + 1
    );

    const chapterResults = await Promise.all(
      chapterNumbers.map((chapterNumber) =>
        fetchAndCacheChapterVerses(testamentType, bookKey, chapterNumber)
      )
    );

    chapterResults.forEach((chapterVerses) => {
      if (Array.isArray(chapterVerses) && chapterVerses.length > 0) {
        allVerses.push(...chapterVerses);
      }
    });

    if (startIndex + BOOK_FALLBACK_BATCH_SIZE < chapterCount) {
      await wait(BOOK_FALLBACK_BATCH_GAP);
    }
  }

  return allVerses;
};

const buildDownloadItems = () => {
  const sections = getStaticBibleSections();

  return sections.flatMap((section) =>
    section.books.map((book) => ({
      id: getBookStorageKey(
        section.testament === TESTAMENT_CONFIG.old.testament ? "old" : "new",
        book.book_name
      ),
      sectionId: section.id,
      testament:
        section.testament === TESTAMENT_CONFIG.old.testament ? "old" : "new",
      bookName: book.book_name.toLowerCase(),
      originalBookName: book.book_name,
    }))
  );
};

const runWithConcurrency = async (items, concurrency, worker) => {
  let currentIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (currentIndex < items.length) {
        const itemIndex = currentIndex;
        currentIndex += 1;

        await worker(items[itemIndex], itemIndex);
      }
    }
  );

  await Promise.all(workers);
};

const readCompleteMarker = () => {
  const marker = readJsonFromLocalStorage(COMPLETE_STORAGE_KEY);

  if (!marker || marker.cacheVersion !== CACHE_VERSION) {
    return null;
  }

  return marker;
};

const saveCompleteMarker = (total) => {
  const marker = {
    cacheVersion: CACHE_VERSION,
    total,
    completed: total,
    percent: 100,
    savedAt: Date.now(),
  };

  writeJsonToLocalStorage(COMPLETE_STORAGE_KEY, marker);

  setToIndexedDb(META_STORE_NAME, {
    id: "complete",
    ...marker,
  });
};

const isCacheMarkedComplete = (total) => {
  const marker = readCompleteMarker();

  return Boolean(
    marker &&
      marker.cacheVersion === CACHE_VERSION &&
      Number(marker.total) === Number(total) &&
      Number(marker.completed) === Number(total) &&
      Number(marker.percent) === 100
  );
};

const getMissingDownloadItems = async (downloadItems) => {
  const missingItems = [];

  for (const item of downloadItems) {
    const cachedBook = await getFromIndexedDb(BOOK_STORE_NAME, item.id);

    if (!cachedBook || !Array.isArray(cachedBook.verses)) {
      missingItems.push(item);
    }
  }

  return missingItems;
};

export const registerTestamentSection = (sectionId, testamentType) => {
  if (sectionId === undefined || sectionId === null) {
    return;
  }

  if (testamentType !== "old" && testamentType !== "new") {
    return;
  }

  SECTION_TYPE_MAP[String(sectionId)] = testamentType;
};

export const getStaticBibleSections = () => {
  const oldSectionId = TESTAMENT_CONFIG.old.sectionId;
  const newSectionId = TESTAMENT_CONFIG.new.sectionId;

  registerTestamentSection(oldSectionId, "old");
  registerTestamentSection(newSectionId, "new");

  return [
    {
      id: oldSectionId,
      testament: TESTAMENT_CONFIG.old.testament,
      books: STATIC_OLD_TESTAMENT_BOOKS.map((bookName) => ({
        book_name: bookName,
      })),
    },
    {
      id: newSectionId,
      testament: TESTAMENT_CONFIG.new.testament,
      books: STATIC_NEW_TESTAMENT_BOOKS.map((bookName) => ({
        book_name: bookName,
      })),
    },
  ];
};

export const getChapterVerses = async (
  testamentOrSection,
  bookName,
  chapterNumber
) => {
  const testamentType = resolveTestamentType(testamentOrSection);
  const bookKey = normalizeBookKey(bookName);

  if (testamentType) {
    const cachedChapterVerses = readCachedVerses(
      testamentType,
      bookKey,
      chapterNumber
    );

    if (cachedChapterVerses && cachedChapterVerses.length > 0) {
      return cachedChapterVerses;
    }

    const memoryWholeBookVerses = readWholeBookMemoryCache(
      testamentType,
      bookKey
    );

    if (memoryWholeBookVerses && memoryWholeBookVerses.length > 0) {
      const chapterVerses = memoryWholeBookVerses.filter(
        (verse) => Number(verse.chapter_number) === Number(chapterNumber)
      );

      if (chapterVerses.length > 0) {
        saveChapterVersesToMemoryOnly(
          testamentType,
          bookKey,
          chapterNumber,
          chapterVerses
        );

        return chapterVerses;
      }
    }

    const indexedDbWholeBookVerses = await readWholeBookIndexedDbCache(
      testamentType,
      bookKey
    );

    if (indexedDbWholeBookVerses && indexedDbWholeBookVerses.length > 0) {
      const chapterVerses = indexedDbWholeBookVerses.filter(
        (verse) => Number(verse.chapter_number) === Number(chapterNumber)
      );

      if (chapterVerses.length > 0) {
        saveChapterVersesToMemoryOnly(
          testamentType,
          bookKey,
          chapterNumber,
          chapterVerses
        );

        return chapterVerses;
      }
    }

    return fetchAndCacheChapterVerses(testamentType, bookKey, chapterNumber);
  }

  return fetchJsonSafely(
    `${backendUrl}/api/sections/${testamentOrSection}/${bookKey}/${chapterNumber}`
  )
    .then((data) => {
      const verses = Array.isArray(data?.verses) ? data.verses : [];

      return verses.map((verse) =>
        normalizeVerseWithChapter(verse, chapterNumber)
      );
    })
    .catch(() => []);
};

export const getBookChapterCount = (testamentOrSection, bookName) => {
  return getChapterCount(bookName);
};

export const getWholeBookVerses = async (testamentOrSection, bookName) => {
  const testamentType = resolveTestamentType(testamentOrSection);
  const bookKey = normalizeBookKey(bookName);

  const sectionId = testamentType
    ? TESTAMENT_CONFIG[testamentType].sectionId
    : testamentOrSection;

  if (testamentType) {
    const memoryCachedBook = readWholeBookMemoryCache(testamentType, bookKey);

    if (memoryCachedBook && memoryCachedBook.length > 0) {
      return memoryCachedBook;
    }

    const indexedDbCachedBook = await readWholeBookIndexedDbCache(
      testamentType,
      bookKey
    );

    if (indexedDbCachedBook && indexedDbCachedBook.length > 0) {
      return indexedDbCachedBook;
    }

    const wholeBookCacheKey = getBookStorageKey(testamentType, bookKey);
    const pendingWholeBookRequests = getGlobalPendingWholeBookRequests();

    if (pendingWholeBookRequests.has(wholeBookCacheKey)) {
      return pendingWholeBookRequests.get(wholeBookCacheKey);
    }

    const requestPromise = (async () => {
      let verses = await fetchWholeBookFromSingleEndpoint(
        testamentType,
        sectionId,
        bookKey
      );

      if (!Array.isArray(verses) || verses.length === 0) {
        verses = await fetchWholeBookByChaptersFallback(testamentType, bookKey);
      }

      if (Array.isArray(verses) && verses.length > 0) {
        await saveWholeBookIndexedDbCache(testamentType, bookKey, verses);
      }

      return Array.isArray(verses) ? verses : [];
    })()
      .catch(() => [])
      .finally(() => {
        pendingWholeBookRequests.delete(wholeBookCacheKey);
      });

    pendingWholeBookRequests.set(wholeBookCacheKey, requestPromise);

    return requestPromise;
  }

  try {
    const data = await fetchJsonSafely(
      `${backendUrl}/api/sections/${sectionId}/${bookKey}`
    );

    const chapters = Array.isArray(data?.chapters) ? data.chapters : [];

    return chapters.reduce((allVerses, chapter, index) => {
      const chapterNumber =
        chapter?.chapter_number ||
        chapter?.chapter ||
        chapter?.chapterNumber ||
        index + 1;

      const chapterVerses = Array.isArray(chapter?.verses)
        ? chapter.verses.map((verse) =>
            normalizeVerseWithChapter(verse, chapterNumber)
          )
        : [];

      return allVerses.concat(chapterVerses);
    }, []);
  } catch (error) {
    return [];
  }
};

export const prefetchWholeBookInBackground = (testamentOrSection, bookName) => {
  const testamentType = resolveTestamentType(testamentOrSection);

  if (!testamentType) {
    return undefined;
  }

  const bookKey = normalizeBookKey(bookName);
  const chapterCount = getBookChapterCount(testamentOrSection, bookKey);

  if (!chapterCount) {
    return undefined;
  }

  const bookPrefetchKey = `${testamentType}:${bookKey}`;
  const bookPrefetchRequests = getGlobalBookPrefetchRequests();

  if (bookPrefetchRequests.has(bookPrefetchKey)) {
    return bookPrefetchRequests.get(bookPrefetchKey);
  }

  const prefetchPromise = getWholeBookVerses(testamentOrSection, bookKey)
    .catch(() => [])
    .finally(() => {
      bookPrefetchRequests.delete(bookPrefetchKey);
    });

  bookPrefetchRequests.set(bookPrefetchKey, prefetchPromise);

  return prefetchPromise;
};

export const getBibleDownloadCacheSnapshot = () => {
  return { ...getStore().status };
};

export const subscribeBibleDownloadCache = (listener) => {
  const store = getStore();

  store.listeners.add(listener);
  listener({ ...store.status });

  return () => {
    store.listeners.delete(listener);
  };
};

export const startBibleDownloadCache = () => {
  const existingPromise = getDownloadPromise();
  const downloadItems = buildDownloadItems();

  if (existingPromise) {
    return existingPromise;
  }

  if (downloadItems.length === 0) {
    setStatus({
      status: "error",
      percent: 0,
      completed: 0,
      total: 0,
      errors: 1,
      message: "No books found.",
    });

    return Promise.resolve(getBibleDownloadCacheSnapshot());
  }

  if (isCacheMarkedComplete(downloadItems.length)) {
    setStatus({
      status: "ready",
      percent: 100,
      completed: downloadItems.length,
      total: downloadItems.length,
      errors: 0,
      message: "Bible cache ready.",
    });

    return Promise.resolve(getBibleDownloadCacheSnapshot());
  }

  const promise = (async () => {
    const savedBookCount = await countIndexedDbStore(BOOK_STORE_NAME);
    const safeSavedBookCount = Math.min(savedBookCount, downloadItems.length);

    setStatus({
      status: "checking",
      percent: Math.round((safeSavedBookCount / downloadItems.length) * 100),
      completed: safeSavedBookCount,
      total: downloadItems.length,
      errors: 0,
      message: "Checking Bible cache...",
    });

    const missingItems = await getMissingDownloadItems(downloadItems);

    if (missingItems.length === 0) {
      saveCompleteMarker(downloadItems.length);

      setStatus({
        status: "ready",
        percent: 100,
        completed: downloadItems.length,
        total: downloadItems.length,
        errors: 0,
        message: "Bible cache ready.",
      });

      setDownloadPromise(null);

      return getBibleDownloadCacheSnapshot();
    }

    let completed = downloadItems.length - missingItems.length;
    let errors = 0;

    setStatus({
      status: "running",
      percent: Math.round((completed / downloadItems.length) * 100),
      completed,
      total: downloadItems.length,
      errors,
      message: "Downloading missing Bible books...",
    });

    await runWithConcurrency(
      missingItems,
      CACHE_DOWNLOAD_CONCURRENCY,
      async (item) => {
        try {
          const verses = await getWholeBookVerses(item.sectionId, item.bookName);

          if (!Array.isArray(verses) || verses.length === 0) {
            errors += 1;
          }
        } catch (error) {
          errors += 1;
        } finally {
          completed += 1;

          setStatus({
            status: "running",
            completed,
            total: downloadItems.length,
            errors,
            percent: Math.max(
              1,
              Math.round((completed / downloadItems.length) * 100)
            ),
            message: `${item.originalBookName} downloaded`,
          });
        }
      }
    );

    if (errors === 0) {
      saveCompleteMarker(downloadItems.length);
    }

    setStatus({
      status: errors > 0 ? "ready_with_errors" : "ready",
      completed: downloadItems.length,
      total: downloadItems.length,
      errors,
      percent: 100,
      message:
        errors > 0
          ? "Bible cache completed with some errors."
          : "Bible cache ready.",
    });

    setDownloadPromise(null);

    return getBibleDownloadCacheSnapshot();
  })().catch((error) => {
    setStatus({
      status: "error",
      errors: 1,
      message: error?.message || "Bible cache failed.",
    });

    setDownloadPromise(null);

    return getBibleDownloadCacheSnapshot();
  });

  setDownloadPromise(promise);
 
  return promise; 
};

export default startBibleDownloadCache;